// Implements: F-IDN-012
/**
 * The orientative strength meter under a new password (v3 `pwField`: four bars and a polite help
 * line). Pure presentation; the server's password policy decides, this only hints.
 *
 * The score is `passwordScore.ts` (the mockup's `strengthOf`). It never praises what the server
 * refuses: below `MINIMUM_PASSWORD_LENGTH` a typed password is the weakest level and is named "too
 * short". `id` names the help line, so the field can point at it with `aria-describedby`.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../i18n";
import styles from "./PasswordStrength.module.css";
import { MINIMUM_PASSWORD_LENGTH, isBelowMinimum, passwordStrength } from "./passwordScore";

const LEVEL_KEYS = [
  "account.password.strengthNone",
  "account.password.strengthWeak",
  "account.password.strengthFair",
  "account.password.strengthGood",
  "account.password.strengthStrong",
] as const;

export interface PasswordStrengthProps {
  /** The identifier of the help line. */
  readonly id: string;
  readonly password: string;
}

export function PasswordStrength({ id, password }: PasswordStrengthProps): ReactElement {
  const { t } = useTranslation();
  const strength = passwordStrength(password);
  const level = t(isBelowMinimum(password) ? "account.password.strengthTooShort" : LEVEL_KEYS[strength]);
  return (
    <>
      <div className={styles.strength} data-strength={strength} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <p className={styles.help} id={id} aria-live="polite">
        {t("account.password.strengthHelp", { level, min: MINIMUM_PASSWORD_LENGTH })}
      </p>
    </>
  );
}
